import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

export interface TrialExportStatus {
  status: "not_available" | "ready";
  generated_at?: string | null;
  trial_ends_at?: string | null;
  format_version?: string;
  capture_count?: number;
  pair_count?: number;
  excluded_count?: number;
  size_bytes?: number;
  first_downloaded_at?: string | null;
  // Set once first_downloaded_at is stamped: the bundle is deleted at this
  // instant (fixed retention window anchored to the FIRST download).
  expires_at?: string | null;
  retention_days_after_first_download?: number;
}

const TRIAL_EXPORT_KEY = ["trial-export"];

export function useTrialExport(tenantId: string) {
  return useQuery({
    queryKey: [...TRIAL_EXPORT_KEY, tenantId],
    queryFn: () => api.get("trial/export").json<TrialExportStatus>(),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

export function useDownloadTrialExport(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const blob = await api.get("trial/export/download").blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "phantm_trial_eval_bundle.zip";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      // The first download stamps first_downloaded_at server-side, which
      // starts the retention countdown — refetch so expires_at appears.
      qc.invalidateQueries({ queryKey: [...TRIAL_EXPORT_KEY, tenantId] });
    },
  });
}
